import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ImageViewableStatus,
    ListInvoiceSearchRequest,
    ListInvoiceSearchResponse,
    ListInvoiceSearchService,
    RelationshipInvoice,
    SearchInvoice
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ClassNames,
    DateFormat,
    forLifeOf,
    getDynamicRouteLink,
    LifecycleAware,
    StatusColor,
    Styling
} from '@lsq-frontend/common-ui';
import {
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
    atlas,
    CUSTOMER_OVERVIEW,
    INVOICES_INVOICE
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GlobalSearchService } from '../../global-search/global-search.service';
import { InvoiceHistoricalImagePreviewModal } from '../../invoices/invoices.models';

@LifecycleAware()
@Component({
    selector: 'invoice-number-results-report',
    template: `
        <div [ngClass]="{ 'min-page-width-medium-up': !(dataSource?.loading$ | async) }">
            <div
                *ngIf="dataSource"
                id="invoice-number-results-report-page-container"
                class="container-fluid page-container"
            >
                <h1>Invoice #</h1>

                <div class="row">
                    <div class="col">
                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="false"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="mtc-header-content">
                                    <div
                                        class="mtc-header-title"
                                        (click)="handleDebtorNameClick(row.debtorKey)"
                                    >
                                        {{ row.debtorName }}
                                    </div>
                                    <div class="mtc-header-sub-title">
                                        <span (click)="handleInvoiceNumberClick(row.invoiceKey)">
                                            Invoice #: {{ row.invoiceNumber }}
                                        </span>
                                        <ng-container
                                            *ngIf="row.imageViewableStatus === isViewableStatus"
                                        >
                                            <br />
                                            <a
                                                class="invoice-image-preview-btn"
                                                href="javascript:void(0)"
                                                [ngStyle]="{ fontSize: '1rem' }"
                                                (click)="handleInvoicePreviewButtonClick(row)"
                                            >
                                                View invoice image
                                            </a>
                                        </ng-container>
                                    </div>
                                </div>
                            </ng-template>

                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div class="card-item-label">
                                        Invoice Date
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.invoiceDate | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Invoice Amount
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.invoiceAmount | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Invoice Age
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.age | days }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Purchase Date
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.purchaseDate | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Payment Date
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.paymentDate | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Payment Amount
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.paymentAmount | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Check #
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.checkNumber }}
                                    </div>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>

                        <lsq-data-table
                            *ngIf="!(responsiveService.smBelow | async)"
                            [dataSource]="dataSource"
                            [hasStickyHeader]="true"
                            [isPaginated]="false"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-column
                                [hidden]="!(shouldDisplayImagePreviewColumn$ | async)"
                                [column]="{
                                    width: '3rem',
                                    sortable: false,
                                    key: 'imageViewableStatus'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div
                                        [ngClass]="'link'"
                                        (click)="handleInvoicePreviewButtonClick(row)"
                                    >
                                        <ng-container
                                            *ngIf="row.imageViewableStatus === isViewableStatus"
                                        >
                                            <lsq-icon
                                                [type]="icons.ImageFile"
                                                [width]="14"
                                                [height]="18"
                                            >
                                            </lsq-icon>
                                        </ng-container>
                                    </div>
                                </ng-template>
                            </lsq-column>

                            <lsq-column
                                [column]="{
                                    header: 'Invoice #',
                                    key: 'invoiceNumber',
                                    truncateTextCss: true,
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div
                                        [ngClass]="'link'"
                                        (click)="handleInvoiceNumberClick(row.invoiceKey)"
                                    >
                                        <span
                                            [innerHTML]="
                                                getInvoiceNumberSpanStream(row.invoiceNumber)
                                                    | async
                                            "
                                        >
                                        </span>
                                    </div>
                                </ng-template>
                            </lsq-column>

                            <lsq-column
                                [column]="{
                                    header: 'Customer',
                                    key: 'debtorName',
                                    truncateTextCss: true,
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div
                                        [ngClass]="'link'"
                                        (click)="handleDebtorNameClick(row.debtorKey)"
                                    >
                                        <span>{{ row.debtorName || 'No Name' }}</span>
                                    </div>
                                </ng-template>
                            </lsq-column>

                            <lsq-date-column
                                [column]="{
                                    header: 'Invoice Date',
                                    key: 'invoiceDate',
                                    sortable: false
                                }"
                            ></lsq-date-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Invoice Amount',
                                    key: 'invoiceAmount',
                                    sortable: false
                                }"
                            ></lsq-currency-column>

                            <lsq-spacer-column
                                [column]="{
                                    key: 'spacer-1',
                                    width: '3rem'
                                }"
                            ></lsq-spacer-column>

                            <lsq-column
                                [column]="{
                                    header: 'Invoice Age',
                                    key: 'age',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    {{ row.age | days }}
                                </ng-template>
                            </lsq-column>

                            <lsq-date-column
                                [column]="{
                                    header: 'Purchase Date',
                                    key: 'purchaseDate',
                                    sortable: false
                                }"
                            ></lsq-date-column>

                            <lsq-date-column
                                [column]="{
                                    header: 'Payment Date',
                                    key: 'paymentDate',
                                    sortable: false
                                }"
                            ></lsq-date-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Payment Amount',
                                    key: 'paymentAmount',
                                    sortable: false
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-spacer-column
                                [column]="{
                                    key: 'spacer-2',
                                    width: '3rem'
                                }"
                            ></lsq-spacer-column>

                            <lsq-column
                                [column]="{
                                    header: 'Check #',
                                    key: 'checkNumber',
                                    sortable: false
                                }"
                            ></lsq-column>
                        </lsq-data-table>

                        <h2
                            *ngIf="(dataSource.totalCount$ | async) > 25"
                            class="h4 text-center font-weight-normal"
                            [style.margin]="'4rem 0'"
                        >
                            Here are the top 25 matches. Try refining your search for better
                            results.
                        </h2>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./invoice-number-results.report.component.scss']
})
export class InvoiceNumberResultsReportComponent implements OnDestroy {
    public shouldDisplayImagePreviewColumn$: Observable<boolean>;
    public dataSource = new LsqApiServiceListSource<
        ListInvoiceSearchResponse,
        ListInvoiceSearchRequest,
        SearchInvoice
    >(this.apiService);

    public classNames = ClassNames;
    public styling = Styling;
    public icons = Icons;
    public dateFormat = DateFormat;
    public statusColor = StatusColor;
    public isViewableStatus = ImageViewableStatus.Viewable;

    constructor(
        public apiService: ListInvoiceSearchService,
        public responsiveService: ResponsiveService,
        private _globalSearchService: GlobalSearchService,
        private _featureFlagService: FeatureFlagService,
        private _modalService: ModalService,
        private _router: Router
    ) {
        this.shouldDisplayImagePreviewColumn$ = this.dataSource.rows$.pipe(
            map((rows) =>
                rows.some(
                    ({ imageViewableStatus }) =>
                        imageViewableStatus === ImageViewableStatus.Viewable
                )
            )
        );

        this._globalSearchService.invoiceNumberSearch$
            .pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                map(
                    (invoiceNumberSearch) =>
                        new ListInvoiceSearchRequest({
                            invoiceNumberFilter: invoiceNumberSearch,
                            take: 25
                        })
                )
            )
            .subscribe((request) => {
                this.dataSource.dispatchRequest(request);
            });
    }

    public ngOnDestroy(): void {}

    public handleInvoicePreviewButtonClick(invoice: RelationshipInvoice): void {
        if (invoice.imageViewableStatus === ImageViewableStatus.Viewable) {
            this._modalService.showModal(new InvoiceHistoricalImagePreviewModal(invoice));
        }
    }

    public async handleInvoiceNumberClick(invoiceKey: Guid): Promise<void> {
        await this._router.navigateByUrl(
            getDynamicRouteLink(getRouterLinkUrlById(atlas, INVOICES_INVOICE), { invoiceKey })
        );
    }

    public async handleDebtorNameClick(debtorKey: Guid): Promise<void> {
        await this._router.navigateByUrl(
            getDynamicRouteLink(getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW), { debtorKey })
        );
    }

    public getInvoiceNumberSpanStream(content: string): Observable<string> {
        return this._globalSearchService.invoiceNumberSearch$.pipe(
            map((invoiceNumberSearch) =>
                invoiceNumberSearch ? this.highlightSearch(content, invoiceNumberSearch) : content
            )
        );
    }

    public highlightSearch(content: string, searchString: string): string {
        return content.replace(
            searchString,
            (match) => `<span class="highlight-search">${match}</span>`
        );
    }
}
